import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box
} from '@chakra-ui/react';
import { MdMergeType, MdCheck, MdClose } from 'react-icons/md';
import { MergeCreationWizard, Modal, DetailedMergeGrid, BulkSelectionBar, BulkEditButton } from "components";
import { useCreateMerge, useDetailedStatusMerges, useApproveMerge, useRejectMerge, useBulkApproveMerges, useBulkRejectMerges } from "hooks/queries/useMerges";
import { useArtists } from "hooks/queries/useArtists";
import { ProcessStatus } from "models/types";
import { ItemModel, MergeModel } from "models";
import { useSetCreateAction } from "contexts/CreateActionContext";

export function MergeOverview() {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(ProcessStatus.PENDING);
  const { data: mergesPagination, isLoading } = useDetailedStatusMerges(selectedStatus);
  const { data: artistsData } = useArtists();
  const createMergeMutation = useCreateMerge();
  const approveMergeMutation = useApproveMerge();
  const rejectMergeMutation = useRejectMerge();
  const bulkApproveMutation = useBulkApproveMerges();
  const bulkRejectMutation = useBulkRejectMerges();
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const setCreateAction = useSetCreateAction();

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMerges, setSelectedMerges] = useState<MergeModel[]>([]);
  const visibleMergesRef = useRef<MergeModel[]>([]);

  // Get merges from pagination data
  const mergeList = mergesPagination?.data || [];
  const hasPendingMerges = mergeList.some(m => m.status === ProcessStatus.PENDING);
  const artistsList = artistsData || [];

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
  };

  const handleMergeModalOpen = useCallback(() => {
    if (createMergeMutation.isPending) {
      return;
    }
    createMergeMutation.reset();
    setIsMergeModalOpen(true);
  }, [createMergeMutation]);

  const handleMergeModalClose = useCallback(() => {
    createMergeMutation.reset();
    setIsMergeModalOpen(false);
  }, [createMergeMutation]);

  const handleMergeSubmit = useCallback((selectedItems: ItemModel[]) => {
    if (selectedItems.length < 2 || createMergeMutation.isPending) {
      return;
    }

    const payload = {
      items: selectedItems.map(item => item.id)
    };

    createMergeMutation.mutate(payload, {
      onSuccess: () => {
        handleMergeModalClose();
      }
    });
  }, [createMergeMutation, handleMergeModalClose]);

  const handleApprove = useCallback((mergeId: string) => {
    approveMergeMutation.mutate(mergeId);
  }, [approveMergeMutation]);

  const handleReject = useCallback((mergeId: string) => {
    rejectMergeMutation.mutate(mergeId);
  }, [rejectMergeMutation]);

  useEffect(() => {
    if (selectionMode) {
      setCreateAction(null);
    } else {
      setCreateAction({
        icon: MdMergeType,
        label: 'Create',
        onClick: handleMergeModalOpen,
        isLoading: createMergeMutation.isPending,
      });
    }

    return () => setCreateAction(null);
  }, [setCreateAction, handleMergeModalOpen, createMergeMutation.isPending, selectionMode]);

  const handleEnterSelectionMode = useCallback(() => {
    if (!hasPendingMerges) return;
    setSelectionMode(true);
    setSelectedMerges([]);
  }, [hasPendingMerges]);

  const handleExitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedMerges([]);
  }, []);

  const handleMergeSelect = useCallback((merge: MergeModel) => {
    setSelectedMerges(prev => {
      const exists = prev.some(m => m.id === merge.id);
      if (exists) {
        return prev.filter(m => m.id !== merge.id);
      }
      return [...prev, merge];
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const pendingMerges = visibleMergesRef.current.filter(m => m.status === ProcessStatus.PENDING);
    setSelectedMerges(pendingMerges);
  }, []);

  const handleFilteredMergesChange = useCallback((merges: MergeModel[]) => {
    visibleMergesRef.current = merges;
  }, []);

  const handleBulkApprove = useCallback(async () => {
    if (selectedMerges.length === 0) return;
    const mergeIds = selectedMerges.map(m => m.id);
    await bulkApproveMutation.mutateAsync(mergeIds);
    handleExitSelectionMode();
  }, [selectedMerges, bulkApproveMutation, handleExitSelectionMode]);

  const handleBulkReject = useCallback(async () => {
    if (selectedMerges.length === 0) return;
    const mergeIds = selectedMerges.map(m => m.id);
    await bulkRejectMutation.mutateAsync(mergeIds);
    handleExitSelectionMode();
  }, [selectedMerges, bulkRejectMutation, handleExitSelectionMode]);

  const pendingVisibleCount = visibleMergesRef.current.filter(m => m.status === ProcessStatus.PENDING).length;

  return (
    <>
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh" mx="auto">
        <DetailedMergeGrid
          merges={mergeList}
          isLoading={isLoading}
          onApprove={handleApprove}
          onReject={handleReject}
          approveLoading={approveMergeMutation.isPending}
          rejectLoading={rejectMergeMutation.isPending}
          defaultStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          defaultAccordionOpen={true}
          availableArtists={artistsList}
          selectionMode={selectionMode}
          selectedMerges={selectedMerges}
          onMergeSelect={handleMergeSelect}
          onFilteredMergesChange={handleFilteredMergesChange}
        />
      </Box>

      <BulkEditButton
        onClick={handleEnterSelectionMode}
        isVisible={!selectionMode && hasPendingMerges}
      />

      {selectionMode && (
        <BulkSelectionBar
          selectedCount={selectedMerges.length}
          totalCount={pendingVisibleCount}
          onSelectAll={handleSelectAll}
          actions={[
            {
              label: 'Approve',
              colorScheme: 'green',
              icon: MdCheck,
              onClick: handleBulkApprove
            },
            {
              label: 'Reject',
              colorScheme: 'red',
              icon: MdClose,
              onClick: handleBulkReject
            }
          ]}
          onCancel={handleExitSelectionMode}
          isLoading={bulkApproveMutation.isPending || bulkRejectMutation.isPending}
          entityName="merge"
        />
      )}
      <Modal
        isOpen={isMergeModalOpen}
        onClose={handleMergeModalClose}
        title="Create Merge"
        content={
          <MergeCreationWizard
            onSubmit={handleMergeSubmit}
            isLoading={createMergeMutation.isPending}
          />
        }
        showCloseButton={false}
        showSubmitButton={false}
        size="4xl"
      />
    </>
  );
}

export default MergeOverview;
